<template>
	<nk-dropdown v-model="dropdownValue" :options="computedDropdownOptions" @update:modelValue="valueChanged" ref="dropdown" :disabled="disabled || settings.disabled ? true : null" :tabindex="tabindex"></nk-dropdown>
</template>

<script>
	import Merge from 'lodash/merge'
	import $sc from '~/plugins/shortcut.collection.js'
	import productFloatingsModify from '~/views/sale/products/request/product-center-modify.vue'
	import productFloatingCombination from '~/components/form/product/product-floating-combination.vue'
	import emptyId from '~/statics/empty-id'
	import { generateUUID } from '@/plugins/helper'
	import productFloatingList from '~/components/form/product/product-floating-jsons-view.vue'
	import _ from 'lodash'

	export default {
		name: 'dropdown-product-floatings',
		emits: ['update:modelValue'],
		props: {
			options: { type: Object, required: true },
			tabindex: { type: Number },
			modelValue: { type: Object },
			disabled: { type: Boolean, default: false }
		},
		watch: {
			options: {
				deep: true,
				immediate: true,
				handler(val) {
					if (!val) return
					const list = val.productFloatingJsons || val.product?.productFloatingJsons || (val.dropdownOptions?.data) || []
					this.settings = {
						...this.settings,
						...val,
						productFloatingJsons: list.length ? [...list] : (this.settings.productFloatingJsons || []),
					}

					if (this.settings.productFloatingJsons?.length && this.settings.productFloatingJsons[0]?.productId)
						this.settings.productId = this.settings.productFloatingJsons[0].productId
					else if (val.productId)
						this.settings.productId = val.productId
					else if (val.product?.id)
						this.settings.productId = val.product.id

					if (this.settings.dropdownOptions)
						this.dropdownOptions = Merge(this.dropdownOptions, this.settings.dropdownOptions)
				},
			},
			modelValue: {
				deep: true,
				immediate: true,
				handler(val, oldVal) {
					this.localValue = _.cloneDeep(val)
				}
			},
		},
		computed: {
			dropdownValue: {
				get() {
					return this.localValue
				},
				set(val) {
					this.localValue = val
					this.valueChanged(val)
				}
			},
			computedDropdownOptions() {
				const list = this.getFloatingJsonsList()
				return {
					...this.dropdownOptions,
					...(this.settings.dropdownOptions || {}),
					getDataFromOptions: true,
					data: list,
					buttons: this.retrieveDropdownButtons(),
				}
			},
		},
		mounted() {
			this.dropdownOptions.buttons = this.retrieveDropdownButtons()
		},
		data() {
			return {
				settings: {
					productFloatingJsons: [],
					productId: emptyId,
					listButtonDisabled: false,
					addButtonDisabled: false,
					combinationButtonDisabled: false,
					hasCombinationButton: false,
					showCombinationButton: false,
					hideListButton: false,
					onAddFloatingHandler: null,
					onChangeHandler: null,
				},
				localValue: null,
				dropdownOptions: {
					rowIdentifier: 'id',
					searchable: true,
					required: true,
					remoteSearch: false,
					placeholder: this.$t('search'),
					display: (item) => (item?.productFloatingValues || []).map(x => x.title).join(' | '),
					findExactMatch: this.findExactMatch,
					clientSearch: this.clientSearch,
					buttons: []
				}
			}
		},
		methods: {
			getFloatingJsonsList() {
				let list = []
				if (this.settings.productFloatingJsons && this.settings.productFloatingJsons.length) {
					list = this.settings.productFloatingJsons
				} else if (this.settings.dropdownOptions?.data && this.settings.dropdownOptions.data.length) {
					list = this.settings.dropdownOptions.data
				} else if (this.options?.productFloatingJsons && this.options.productFloatingJsons.length) {
					list = this.options.productFloatingJsons
				} else if (this.options?.product?.productFloatingJsons && this.options.product.productFloatingJsons.length) {
					list = this.options.product.productFloatingJsons
				} else if (this.options?.dropdownOptions?.data) {
					list = this.options.dropdownOptions.data
				}
				return (list || []).filter(x => x && x.productFloatingValues && x.productFloatingValues.length > 0)
			},
			retrieveDropdownButtons() {
				const buttons = []

				if (this.settings.hasCombinationButton || this.settings.showCombinationButton) {
					buttons.push({
						title: this.$t('change-log.operations.added'),
						shortcut: $sc.find('enter'),
						isPrimary: true,
						disabled: this.settings.combinationButtonDisabled,
						element: () => this.$el,
						icon: 'plus',
						handle: (button, page, e) => {
							this.showCombinationAdd()
						}
					})
				}

				if (!this.settings.hideListButton) {
					buttons.push({
						title: this.$t('list'),
						shortcut: (this.settings.hasCombinationButton || this.settings.showCombinationButton) ? $sc.find('select') : $sc.find('enter'),
						isPrimary: !(this.settings.hasCombinationButton || this.settings.showCombinationButton),
						disabled: this.settings.listButtonDisabled,
						element: () => this.$el,
						icon: 'list-ul',
						handle: (button, page, e) => {
							this.showFloatingsList()
						}
					})
				}

				if (!this.settings.hasCombinationButton && !this.settings.showCombinationButton) {
					buttons.push({
						title: this.$t('new'),
						shortcut: $sc.find('ctrl-insert'),
						isPrimary: false,
						disabled: this.settings.addButtonDisabled,
						toolip: this.settings.addButtonDisabled ? this.$t('sale.factor.m.product-floating-add-button-disabled') : '',
						element: () => this.$el,
						icon: 'plus',
						handle: (button, page, e) => {
							this.showFloatingsAdd()
						}
					})
				}

				return buttons
			},
			valueChanged(val) {

				if (this.settings.onChangeHandler && typeof this.settings.onChangeHandler === 'function')
					this.settings.onChangeHandler(val)

				this.$emit('update:modelValue', val)
			},
			findExactMatch(list, text) {
				var filtered = (list || []).filter(x => (x.productFloatingValues || []).map(v => v.title).join(' | ') === text)
				if (filtered.length === 1) {
					return filtered[0]
				}
			},
			clientSearch(keyword, data) {
				if (!keyword || typeof keyword !== 'string') return data || []

				for (const word of keyword.getWords())
					data = (data || []).filter(x => (x.productFloatingValues || []).map(productFloatingValue => productFloatingValue.title).some(floatingValue => floatingValue && floatingValue.includes(word)))

				return data
			},
			showFloatingsList() {
				this.showModal({
					component: productFloatingList,
					options: {
						productFloatingJsons: this.getFloatingJsonsList(),
					},
				})
					.then(res => {
						this.dropdownValue = res
					})
					.catch(() => { })
			},
			showFloatingsAdd() {
				this.showModal({
					component: productFloatingsModify,
					data: [],
					options: {
						productId: this.settings.productId,
					},
				})
					.then(res => {
						res.id = res.id ? res.id : generateUUID()
						res.rowState = res.rowState ? res.rowState : 'new'
						res.displayOrder = this.settings.productFloatingJsons.length + 1
						this.settings.productFloatingJsons.push(res)
						this.dropdownValue = res

						if (this.settings.onAddFloatingHandler && typeof this.settings.onAddFloatingHandler === 'function')
							this.settings.onAddFloatingHandler(res, this.settings.productFloatingJsons)
					})
					.catch(() => { })
			},
			getCombinationData(json) {
				if (!json?.productFloatingValues?.length)
					return null

				const productFloatings = this.$store?.getters?.['productFloatings/all'] || []

				const result = json.productFloatingValues
					.filter(value => (value?.productFloating?.id || value?.productFloatingId) && value.title)
					.map(value => {
						const floatingId = value?.productFloating?.id || value?.productFloatingId
						const floating = productFloatings.find(x => x.id === floatingId)
						const valueType = floating?.valueType || value?.productFloating?.floatingValType || (value.id && value.id !== emptyId ? 'Selective' : 'Manual')
						const isSelective = valueType === 'Selective'

						return {
							productFloatingId: floatingId,
							productFloatingValueId: isSelective ? (value.id || emptyId) : emptyId,
							productFloatingValueTitle: value.title,
							productFloatingName: floating?.name || value.productFloating?.name || '',
							productFloatingType: valueType,
						}
					})

				return result.length ? result : null
			},
			showCombinationAdd() {
				this.showModal({
					component: productFloatingCombination,
					data: this.getCombinationData(this.localValue || this.dropdownValue),
					options: {
						productId: this.settings.productId,
					},
				})
					.then(res => {
						if (!res || !res.length)
							return

						let matchingItem = this.findMatchingFloatingJson(this.settings.productFloatingJsons, res)
						if (!matchingItem) {
							matchingItem = this.createFloatingJsonFromData(res)
							this.settings.productFloatingJsons.push(matchingItem)

							if (this.settings.onAddFloatingHandler && typeof this.settings.onAddFloatingHandler === 'function')
								this.settings.onAddFloatingHandler(matchingItem, this.settings.productFloatingJsons)
						}

						this.dropdownValue = matchingItem
					})
					.catch(() => { })
			},
			createFloatingJsonFromData(res) {
				return {
					id: generateUUID(),
					productId: this.settings.productId || emptyId,
					rowState: 'unchanged',
					displayOrder: (this.settings.productFloatingJsons?.length || 0) + 1,
					productFloatingValues: res.map((item, index) => ({
						id: item.productFloatingValueId && item.productFloatingValueId !== emptyId ? item.productFloatingValueId : emptyId,
						title: item.productFloatingValueTitle,
						displayOrder: index,
						rowState: 'unchanged',
						productFloating: {
							id: item.productFloatingId,
							name: item.productFloatingName,
							floatingValType: item.productFloatingType,
						},
					})),
				}
			},
			findMatchingFloatingJson(list, productFloatingData) {
				if (!list?.length || !productFloatingData?.length)
					return null

				return list.find(json => {
					const jsonValues = (json.productFloatingValues || []).filter(v => v && v.title)
					if (jsonValues.length !== productFloatingData.length)
						return false

					return productFloatingData.every(item =>
						jsonValues.some(value => {
							const floatingId = value?.productFloating?.id || value?.productFloatingId
							if (floatingId && item.productFloatingId && floatingId !== item.productFloatingId)
								return false

							const isManual = item.productFloatingType === 'Manual' || item.productFloatingValueId === emptyId
							if (isManual)
								return value.title === item.productFloatingValueTitle

							return (value.id && item.productFloatingValueId && value.id === item.productFloatingValueId)
								|| value.title === item.productFloatingValueTitle
						})
					)
				}) || null
			},
		}
	}
</script>

<style></style>